import type * as React from 'react'

import { cn } from '../../lib/utils'
import { KuficGridMark } from '../kufic/kufic-grid-mark'

/**
 * "There is nothing here yet", written so it teaches the next action
 * (DESIGN.md "Empty states and charts"): a title, ONE sentence that says what
 * fills this place, and the action that does it.
 *
 * - `variant="compact"` (default): centred, for a table, a list or a lines
 *   band that came back empty.
 * - `variant="page"`: start-aligned with a larger title, for a whole section
 *   or screen with nothing in it yet.
 * - `tone="committed"`: the deep ink surface (`--brand-deep`), for a FIRST-RUN
 *   empty state only, the one dark fill a working screen may carry beside the
 *   spine ("The Two Dark Fills Rule"). Controls inside read
 *   `data-surface="committed"` for their on-deep colours.
 *
 * The mark (`data-slot="empty-state-mark"`) sits above the title: the ruled
 * 5 by 3 grid with the framed nuqta in the reading-start cell
 * (`KuficGridMark`). It is decoration and `aria-hidden`. `icon` is accepted
 * for older callers and no longer drawn: every empty state carries the grid.
 *
 * `title` is required and `description` is not, because a title alone is a
 * complete empty state and a description alone is not.
 */
function EmptyState({
  title,
  description,
  action,
  variant = 'compact',
  tone = 'default',
  className,
}: {
  title: string
  description?: string
  /** @deprecated The grid mark replaced the icon; kept so older callers compile. */
  icon?: React.ReactNode
  action?: React.ReactNode
  variant?: 'compact' | 'page'
  tone?: 'default' | 'committed'
  className?: string
}) {
  const committed = tone === 'committed'

  return (
    <div
      data-slot="empty-state"
      data-variant={variant}
      data-tone={tone}
      data-surface={committed ? 'committed' : undefined}
      className={cn(
        'animate-enter flex flex-col gap-2.5',
        variant === 'compact' ? 'items-center px-6 py-10 text-center' : 'items-start p-8',
        committed && 'bg-brand-deep text-brand-deep-foreground rounded-md',
        className,
      )}
    >
      <span data-slot="empty-state-mark" aria-hidden="true" className="mb-1.5">
        <KuficGridMark tone={tone} />
      </span>
      <p
        data-slot="empty-state-title"
        className={cn(
          variant === 'page' ? 'text-lg font-semibold' : 'text-section-title',
          committed ? 'text-brand-deep-foreground' : 'text-foreground',
        )}
      >
        {title}
      </p>
      {description ? (
        <p
          data-slot="empty-state-description"
          className={cn(
            'text-body max-w-[52ch]',
            committed ? 'text-brand-deep-muted' : 'text-muted-foreground',
          )}
        >
          {description}
        </p>
      ) : null}
      {action ? (
        <div data-slot="empty-state-action" className="mt-1.5 flex flex-wrap items-center gap-2">
          {action}
        </div>
      ) : null}
    </div>
  )
}

export { EmptyState }
